import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import {
  AZURE_SUBSCRIPTION_ID_DUPLICATION,
  AZURE_SUBSCRIPTION_ID_VALIDATION
} from 'components/common/mapped-entities/azure/authentication/azure-validation-utils'
import { type TeamExternalIntegrationAzure } from 'types/api/status-checks/status-check'

export enum AzureAuthRadioChoice {
  Existing = 'existing',
  New = 'new'
}

export interface AzureAuthenticationFormValuesNew {
  subscriptionId: string
}

export interface AzureAuthenticationFormValuesExisting
  extends AzureAuthenticationFormValuesNew {
  authRadioChoice: AzureAuthRadioChoice | undefined
}

/**
 * Builds the Yup schema for Azure authentication form validation.
 *
 * `isNewAuthOnly` is required because there are two different form shapes:
 * - `true`: form only allows creating a new authentication.
 * - `false`: form supports selecting existing or creating new authentication.
 *   AzureAuthRadioChoice is expected to be present in the consuming form.
 */
function getAzureAuthenticationFormSchema({
  integrations,
  isNewAuthOnly
}: {
  integrations: TeamExternalIntegrationAzure[]
  isNewAuthOnly: boolean
}) {
  const integrationsSet = new Set(
    integrations.map(({ domain }) => domain.toUpperCase())
  )

  const baseSubscriptionIdSchema = yup
    .string()
    .required()
    .matches(AZURE_SUBSCRIPTION_ID_VALIDATION.regex, {
      message: AZURE_SUBSCRIPTION_ID_VALIDATION.message
    })

  const newSubscriptionIdSchema = baseSubscriptionIdSchema.test(
    'not-duplicate',
    AZURE_SUBSCRIPTION_ID_DUPLICATION.message,
    (value) => AZURE_SUBSCRIPTION_ID_DUPLICATION.test(value, integrationsSet)
  )

  const existingSubscriptionIdSchema = baseSubscriptionIdSchema.test(
    'exists',
    'Authentication not set',
    (value) => value !== undefined && integrationsSet.has(value.toUpperCase())
  )

  if (isNewAuthOnly) {
    return yup.object({
      subscriptionId: newSubscriptionIdSchema
    })
  }

  return yup.object({
    authRadioChoice: yup
      .string()
      .oneOf(Object.values(AzureAuthRadioChoice))
      .nullable(),
    subscriptionId: baseSubscriptionIdSchema.when('authRadioChoice', {
      is: AzureAuthRadioChoice.Existing,
      then: existingSubscriptionIdSchema,
      otherwise: newSubscriptionIdSchema
    })
  })
}

/**
 * Provides form initializers for when only creating new authentication is allowed.
 */
export function getAzureAuthenticationFormInitializersNew(
  integrations: TeamExternalIntegrationAzure[]
) {
  return {
    defaultValues: {
      subscriptionId: ''
    } satisfies AzureAuthenticationFormValuesNew,
    resolver: yupResolver(
      getAzureAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: true
      })
    )
  }
}

/**
 * Provides form initializers for when the option to add an existing authentication is available.
 * `existingSubscriptionId` should be provided only if we're editing an existing authentication and
 * want to pre-populate the form with that subscription ID.
 */
export function getAzureAuthenticationFormInitializersExisting({
  existingSubscriptionId,
  integrations
}: {
  existingSubscriptionId?: string
  integrations: TeamExternalIntegrationAzure[]
}) {
  const defaultValues: AzureAuthenticationFormValuesExisting =
    integrations.length
      ? {
          authRadioChoice: AzureAuthRadioChoice.Existing,
          subscriptionId:
            existingSubscriptionId ?? integrations[0]?.domain ?? ''
        }
      : {
          authRadioChoice: undefined,
          subscriptionId: ''
        }

  return {
    defaultValues,
    resolver: yupResolver(
      getAzureAuthenticationFormSchema({
        integrations,
        isNewAuthOnly: false
      })
    )
  }
}
